import { useEffect, useState } from "react";
import { itemsApi, businessesApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { RefreshCcw, Loader2, Trash2, Plus, Package } from "lucide-react";

export default function Items() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState("");
  const [items, setItems] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", code: "", rate: 0, account_code: "", item_type: "item" });

  const biz = businesses.find((b) => b.id === bizId);

  useEffect(() => {
    businessesApi.list().then((b) => {
      setBusinesses(b);
      setBizId(isMaster(user) ? (b[0]?.id || "") : userBusinessIds(user)[0] || "");
    });
  }, [user]);

  const load = () => { if (bizId) itemsApi.list(bizId).then(setItems); };
  useEffect(() => { load(); }, [bizId]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await itemsApi.syncFromAccounting(bizId);
      toast.success(`Synced ${res.total} items (+${res.created} new, ~${res.updated} updated)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await itemsApi.upsert({ ...form, rate: parseFloat(form.rate) || 0 }, bizId);
      toast.success("Item saved");
      setForm({ name: "", code: "", rate: 0, account_code: "", item_type: "item" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await itemsApi.del(id);
    load();
  };

  const filtered = items.filter((v) => !search || v.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold text-espresso" data-testid="items-title">Items &amp; services</h1>
          <p className="text-espresso-ink/60 mt-2">Sync your product/service catalogue from {biz?.accounting === "xero" ? "Xero" : "Zoho Books"}. Later, map extracted line items to these.</p>
        </div>
        <div className="flex gap-2">
          {isMaster(user) && (
            <select data-testid="items-biz" value={bizId} onChange={(e) => setBizId(e.target.value)} className="px-3 py-2 bg-white border border-canvas-border text-sm brand-focus">
              {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
        </div>
      </div>
      <div className="text-xs text-espresso-ink/50">
        Item sync runs from <b>Accounting → Master sync</b>.
      </div>

      {!biz?.connected && (
        <div className="p-3 bg-status-pending/10 border border-status-pending/30 text-status-pending text-sm">
          Connect {biz?.accounting === "xero" ? "Xero" : "Zoho Books"} first to enable item sync.
        </div>
      )}

      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items…" className="w-full max-w-sm px-3 py-2 bg-white border border-canvas-border text-sm brand-focus" data-testid="items-search" />

      <form onSubmit={save} className="bg-white border border-canvas-border p-6 shadow-soft grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <div className="md:col-span-2"><label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-3 py-2 border border-canvas-border brand-focus" data-testid="item-name" /></div>
        <div><label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Code/SKU</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-full mt-1 px-3 py-2 border border-canvas-border brand-focus" data-testid="item-code" /></div>
        <div><label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Rate</label><input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="w-full mt-1 px-3 py-2 border border-canvas-border brand-focus" data-testid="item-rate" /></div>
        <div><label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Type</label>
          <select value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })} className="w-full mt-1 px-3 py-2 border border-canvas-border brand-focus" data-testid="item-type">
            <option value="item">Item</option>
            <option value="service">Service</option>
          </select>
        </div>
        <button data-testid="item-save" className="inline-flex items-center justify-center gap-2 py-2 bg-espresso text-canvas font-semibold hover:bg-espresso-light"><Plus className="w-4 h-4" /> Add</button>
      </form>

      <div className="bg-white border border-canvas-border overflow-hidden shadow-soft">
        {filtered.length === 0 ? (
          <div className="p-14 text-center text-espresso-ink/50">No items yet — sync from accounting or add manually.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-canvas text-xs uppercase tracking-wider text-espresso-ink/60">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-right px-4 py-3">Rate</th>
                <th className="text-left px-4 py-3">Account</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-t border-canvas-border">
                  <td className="px-4 py-3 font-medium text-espresso">
                    <div className="flex items-center gap-2"><Package className="w-3.5 h-3.5 text-gold" /> {it.name}
                      {it.external_id && <span className="ml-1 text-[10px] uppercase tracking-widest bg-canvas-border text-espresso-ink/70 px-1.5 py-0.5 rounded-sm">SYNCED</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-espresso-ink font-mono text-xs">{it.code || "—"}</td>
                  <td className="px-4 py-3 text-espresso-ink capitalize">{it.item_type || "item"}</td>
                  <td className="px-4 py-3 text-espresso-ink text-right">R {(it.rate || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-espresso-ink text-xs font-mono">{it.account_code || "—"}</td>
                  <td className="px-3 py-3"><button onClick={() => del(it.id)} className="text-status-duplicate hover:opacity-70"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
